import { Columns, CrosstabAnalysisV2 } from 'api/data/analyses';
import { isAnalysisInputValid } from 'helpers/analysis';
import { ActionTypes, VariablesDataSelectItems } from 'store/data/analyses';

import { CrosstabViewV2 } from './CrosstabViewV2';
import { CrosstabConfigV2 } from './CrosstabConfigV2';

import { InvalidAnalysis } from '..';
import { AnalysisContainer, AnalysisViewContainer } from '../UI';
import { MediaQueries } from 'environment';
import {
	useFetchAnalysis,
	useAnalysesActiveColum,
	useFullscreenAnalysis,
	useAnalysisActiveTab,
	useTranslation
} from 'hooks/store';
import { useMediaQuery } from 'hooks/utils';
import { withMemo } from 'helpers/HOCs';
import { Loader } from 'components/UI/Loader';

interface Props {
	analysis: CrosstabAnalysisV2;
	variablesDataSelectItems: VariablesDataSelectItems;
	exporting: boolean;
}

function Component({ analysis, variablesDataSelectItems, exporting }: Props) {
	const { translate } = useTranslation();

	const validInput =
		analysis.input.variables.rowVariable && analysis.input.variables.columnVariable;

	const { loading } = useFetchAnalysis({
		id: analysis.id,
		actionType: ActionTypes.GET_CROSSTAB_V2,
		...(validInput
			? { refetchDependencies: ['input.variables'] }
			: { refetchDependencies: [] }),
		initialFetchDependency: null
	});
	const [activeColumn] = useAnalysesActiveColum();
	const analysisValidInput = isAnalysisInputValid(analysis);
	const isMobileDevice = useMediaQuery(
		`only screen and ${MediaQueries.minWidth.xs} and ${MediaQueries.maxWidth.sm}`
	);
	const [fullscreen, setFullscreenAnalysis] = useFullscreenAnalysis();
	const [activeTab] = useAnalysisActiveTab(analysis.id);

	const handleFullScreen = () => {
		if (
			(activeColumn !== Columns.OneColumn && !isMobileDevice) ||
			(activeColumn === Columns.OneColumn && isMobileDevice)
		) {
			setFullscreenAnalysis({ fullscreen: analysis.id });
		}
	};

	if (analysisValidInput) {
		return (
			<AnalysisContainer onClick={() => handleFullScreen()} activeColumn={activeColumn}>
				{analysis.fetched && !loading ? (
					<CrosstabViewV2
						key={activeTab}
						analysis={analysis}
						exporting={exporting}
						loading={loading}
					/>
				) : (
					<AnalysisViewContainer isConfigPanelOpen={analysis.options.configPanel.open}>
						<Loader primary />
					</AnalysisViewContainer>
				)}
				{(analysis.options.configPanel.open &&
					activeColumn === Columns.OneColumn &&
					!isMobileDevice) ||
				(analysis.options.configPanel.open && fullscreen) ? (
					<CrosstabConfigV2
						analysis={analysis}
						variablesDataSelectItems={variablesDataSelectItems}
						loading={loading}
					/>
				) : null}
			</AnalysisContainer>
		);
	}

	return (
		<AnalysisContainer invalid>
			<InvalidAnalysis
				{...(!analysisValidInput
					? {
							headerMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputHeader
							)
					  }
					: {})}
				{...(!analysisValidInput
					? {
							bodyMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputBody
							)
					  }
					: {})}
				activeColumn={activeColumn}
				fullScreen={!!fullscreen}
				isConfigPanelOpen={analysis.options.configPanel.open}
				category
			/>
			{(analysis.options.configPanel.open &&
				activeColumn === Columns.OneColumn &&
				!isMobileDevice) ||
			(analysis.options.configPanel.open && (analysis.activeTab === 1 || fullscreen)) ? (
				<CrosstabConfigV2
					analysis={analysis}
					variablesDataSelectItems={variablesDataSelectItems}
					loading={loading}
				/>
			) : null}
		</AnalysisContainer>
	);
}

export const CrosstabV2 = withMemo(Component, [
	'analysis',
	'variablesDataSelectItems',
	'exporting'
]);
